import React, { useState } from 'react';
import { FaCloudUploadAlt, FaFileExcel, FaDownload, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';

const SellerCatalogUpload = () => {
    const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedProducts, setUploadedProducts] = useState([]);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploadStatus('uploading');
        setUploadProgress(0);

        // Simulate upload progress
        const interval = setInterval(() => {
            setUploadProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setUploadStatus('success');
                    setUploadedProducts([
                        { name: 'Handwoven Silk Saree', sku: 'SAR-001', status: 'success' },
                        { name: 'Brass Decorative Lamp', sku: 'LMP-002', status: 'success' },
                        { name: 'Pattachitra Painting', sku: 'ART-003', status: 'error', error: 'Missing price' },
                        { name: 'Silver Anklet', sku: 'JWL-004', status: 'success' },
                        { name: 'Cotton Handloom', sku: 'TEX-005', status: 'success' },
                    ]);
                    return 100;
                }
                return prev + 10;
            });
        }, 200);
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Catalog Upload</h1>
                <p className="text-gray-600">Bulk upload your product catalog using Excel/CSV files</p>
            </div>

            {/* Download Template Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <FaFileExcel className="text-3xl text-green-600" />
                        <div>
                            <p className="font-semibold text-gray-800">Download Template</p>
                            <p className="text-sm text-gray-600">Use our Excel template to ensure correct formatting</p>
                        </div>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                        <FaDownload /> Download Template
                    </button>
                </div>
            </div>

            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Upload Catalog File</h2>

                {uploadStatus === 'idle' && (
                    <label className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition block">
                        <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileUpload}
                            className="hidden"
                        />
                        <FaCloudUploadAlt className="text-6xl text-gray-400 mx-auto mb-4" />
                        <p className="text-lg font-semibold text-gray-700">Drop your file here or click to browse</p>
                        <p className="text-sm text-gray-500 mt-2">Supports: XLSX, XLS, CSV (Max 10MB)</p>
                    </label>
                )}

                {uploadStatus === 'uploading' && (
                    <div className="text-center py-8">
                        <FaSpinner className="text-4xl text-blue-600 animate-spin mx-auto mb-4" />
                        <p className="text-lg font-semibold text-gray-700">Uploading...</p>
                        <div className="w-full bg-gray-200 rounded-full h-3 mt-4 max-w-md mx-auto">
                            <div
                                className="bg-blue-600 h-3 rounded-full transition-all duration-200"
                                style={{ width: `${uploadProgress}%` }}
                            />
                        </div>
                        <p className="text-sm text-gray-500 mt-2">{uploadProgress}% complete</p>
                    </div>
                )}

                {uploadStatus === 'success' && (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaCheck className="text-3xl text-green-600" />
                        </div>
                        <p className="text-lg font-semibold text-green-700">Upload Complete!</p>
                        <p className="text-sm text-gray-500 mt-2">{uploadedProducts.filter(p => p.status === 'success').length} products uploaded successfully</p>
                        <button
                            onClick={() => setUploadStatus('idle')}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Upload Another File
                        </button>
                    </div>
                )}
            </div>

            {/* Upload Results */}
            {uploadedProducts.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b">
                        <h2 className="text-lg font-semibold text-gray-800">Upload Results</h2>
                    </div>
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {uploadedProducts.map((product, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{product.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">{product.sku}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {product.status === 'success' ? (
                                            <span className="flex items-center gap-1 text-green-600">
                                                <FaCheck /> Success
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-red-600">
                                                <FaTimes /> Failed
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.error || 'Uploaded successfully'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Instructions */}
            <div className="mt-6 bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-3">Upload Instructions</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Download the template and fill in your product details</li>
                    <li>• Required fields: Product Name, SKU, Price, Category, Stock</li>
                    <li>• Optional: Description, Images (URLs), Dimensions, Weight</li>
                    <li>• Maximum 500 products per upload</li>
                    <li>• Review the results and fix any errors before publishing</li>
                </ul>
            </div>
        </div>
    );
};

export default SellerCatalogUpload;
